import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Spin } from 'antd';
import { useTranslation } from 'react-i18next';

import { complianceKb } from 'lib/api';
import { COMPLIANCE_KB_BASE } from 'lib/constants';

import * as S from './ComplianceStyles';

const CONTAINER_ID = 'compliance-kb-chat';
const SCRIPT_ID = 'compliance-kb-embed';

type Status = 'idle' | 'loading' | 'ready' | 'error';

type ComplianceChatModalProps = {
  visible: boolean;
  onClose: () => void;
};

/**
 * Modal que hospeda o chat de Compliance da BWG.
 *
 * Usa o modo inline do embed (data-mode="inline" + data-container), e entrega a
 * identidade por window.ComplianceKbEmbed.identify(). O modo bolha e o iframe
 * direto não servem aqui: o direto exige HTML gerado por request, e este front é
 * um build estático; e a identidade chega por Bearer token, não por cookie de
 * sessão, então data-token-url não daria conta.
 *
 * O iframe é montado uma única vez e sobrevive ao fechar/reabrir do modal
 * ENQUANTO o componente continuar montado. Isso preserva a conversa e evita
 * queimar um token novo a cada abertura — o JWT é de uso único e expira em ~60s.
 *
 * Esse componente só existe dentro da rota /feed (via Sidebar), então navegar
 * pra outra tela desmonta tudo: o React destrói o <div id="compliance-kb-chat">
 * (container real do iframe), mas a tag <script id="compliance-kb-embed"> e o
 * global window.ComplianceKbEmbed foram anexados fora da árvore do React
 * (document.body.appendChild) e sobrevivem ao desmonte. Sem a limpeza abaixo,
 * ao voltar pro /feed o componente remonta com um container novo, mas
 * loadEmbedScript() encontra a tag+global antigos e não recarrega nada — o
 * embed.js fica vinculado ao container antigo (já removido do DOM) e o widget
 * para de conectar. O cleanup no unmount garante que cada mount comece do zero.
 */
const ComplianceChatModal: React.VFC<ComplianceChatModalProps> = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('idle');
  const startedRef = useRef(false);

  const loadEmbedScript = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        const existing = document.getElementById(SCRIPT_ID);

        // Só reaproveita a tag se o script realmente rodou — a presença do
        // global é a prova. Uma tag que sobrou de uma tentativa falha faria o
        // retry resolver na hora sem recarregar nada.
        if (existing && window.ComplianceKbEmbed) {
          resolve();
          return;
        }
        existing?.remove();

        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = `${COMPLIANCE_KB_BASE}/embed.js`;
        script.defer = true;
        script.dataset.base = COMPLIANCE_KB_BASE;
        script.dataset.mode = 'inline';
        script.dataset.container = `#${CONTAINER_ID}`;
        script.onload = () => resolve();
        script.onerror = () => {
          script.remove();
          reject(new Error(`Falha ao carregar ${script.src}`));
        };

        document.body.appendChild(script);
      }),
    []
  );

  const start = useCallback(async () => {
    startedRef.current = true;
    setStatus('loading');

    const result = await complianceKb.getEmbedToken();
    if (!result.ok) {
      startedRef.current = false;
      setStatus('error');
      console.error('Compliance: falha ao obter o token do embed', result.status, result.error);
      return;
    }

    try {
      await loadEmbedScript();

      // Sem o global não há como entregar a identidade. Tratar como erro em vez
      // de seguir para 'ready' evita o modal abrir vazio, sem nada e sem aviso.
      if (!window.ComplianceKbEmbed) {
        throw new Error('embed.js carregou sem expor window.ComplianceKbEmbed');
      }

      window.ComplianceKbEmbed.identify(result.data.token);
      setStatus('ready');
    } catch (error) {
      startedRef.current = false;
      setStatus('error');
      console.error('Compliance: falha ao iniciar o embed', error);
    }
  }, [loadEmbedScript]);

  useEffect(() => {
    if (visible && !startedRef.current) start();
  }, [visible, start]);

  // Desfaz o que loadEmbedScript() fez fora da árvore do React, pra um
  // remount (ex.: sair do /feed e voltar) sempre começar do zero, com o
  // script vinculado ao container novo em vez de reaproveitar um global
  // já preso a um <div> que o React acabou de destruir.
  useEffect(
    () => () => {
      document.getElementById(SCRIPT_ID)?.remove();
      delete window.ComplianceKbEmbed;
    },
    []
  );

  return (
    <Modal
      centered
      forceRender
      visible={visible}
      width={S.CHAT_WIDTH}
      footer={null}
      /* o header com título e menu de opções vem dentro do iframe, então o modal
         não desenha o seu próprio. Esc e clique no fundo continuam fechando. */
      closable={false}
      bodyStyle={{ padding: 0, borderRadius: 16, overflow: 'hidden' }}
      onCancel={onClose}
    >
      <S.ChatSlot>
        <S.EmbedTarget id={CONTAINER_ID} />

        {status !== 'ready' && (
          <S.Overlay>
            {status === 'error' ? (
              <>
                <p>{t('compliance.loadError')}</p>
                <Button type="primary" onClick={start}>
                  {t('compliance.retry')}
                </Button>
              </>
            ) : (
              <Spin size="large" />
            )}
          </S.Overlay>
        )}
      </S.ChatSlot>
    </Modal>
  );
};

export default ComplianceChatModal;
